import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ProductCard } from "@itaas/pep-contracts";
import { getProducts, submitLead } from "../api/pep-api";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { usePepI18n } from "../i18n/usePepI18n";
import { labelStatus, productDisplayName, statusBadgeClass } from "../utils/labels";
import { filterPrelaunchProducts } from "../utils/product-status";
import { getAffiliateCodeForLead } from "../utils/affiliate-attribution";

export function PreRegisterPage() {
  const { locale } = usePepI18n();
  const [params] = useSearchParams();
  const preselect = params.get("product") ?? "";

  const [products, setProducts] = useState<ProductCard[]>([]);
  const [selected, setSelected] = useState<string[]>(preselect ? [preselect] : []);
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getProducts()
      .then((list) => {
        setProducts(list);
        const prelaunch = filterPrelaunchProducts(list);
        if (preselect && prelaunch.some((p) => p.slug === preselect)) {
          setSelected([preselect]);
        } else if (!preselect && prelaunch.length === 1) {
          setSelected([prelaunch[0].slug]);
        }
      })
      .catch(console.error);
  }, [preselect]);

  const prelaunchProducts = useMemo(() => filterPrelaunchProducts(products), [products]);

  const selectedLabels = selected
    .map((slug) => prelaunchProducts.find((p) => p.slug === slug))
    .filter((p): p is ProductCard => Boolean(p))
    .map((p) => productDisplayName(p, locale));

  function toggle(slug: string) {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selected.length) {
      setError(
        locale === "en"
          ? "Select at least one product."
          : "Sélectionnez au moins un produit.",
      );
      return;
    }
    setError(null);
    setSubmitting(true);

    void submitLead({
      company,
      name: name.trim() || company,
      email,
      phone,
      country: "RDC",
      type: "preregister",
      products: selected,
      message: message.trim() || undefined,
      locale,
      source: "pep-preregister",
      affiliateCode: getAffiliateCodeForLead(),
    })
      .then(() => setSent(true))
      .catch(() =>
        setError(
          locale === "en" ? "Submission failed. Please retry." : "Échec d'envoi. Réessayez.",
        ),
      )
      .finally(() => setSubmitting(false));
  }

  return (
    <SiteLayout>
      <SeoHead
        title={
          locale === "en"
            ? "Pre-register for upcoming ITaaS modules"
            : "Préinscription — modules ITaaS à venir"
        }
        description={
          locale === "en"
            ? "Join the waitlist for ITaaS modules in development — be notified at launch."
            : "Inscrivez-vous sur la liste d'attente des modules ITaaS en développement — soyez informé au lancement."
        }
        canonicalPath="/preregister"
        locale={locale}
      />
      <div className="pep-page-hero py-4 mb-0">
        <div className="container">
          <h1 className="display-6 fw-bold mb-2">
            {locale === "en" ? "Pre-registration" : "Préinscription"}
          </h1>
          <p className="lead mb-0 opacity-90 col-lg-8">
            {locale === "en"
              ? "These modules are not available yet. Leave your details — our team will contact you for early access or launch notification."
              : "Ces modules ne sont pas encore disponibles. Laissez vos coordonnées — notre équipe vous recontacte pour un accès anticipé ou une notification de lancement."}
          </p>
        </div>
      </div>

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="alert alert-warning border-0 shadow-sm mb-4" role="status">
              <p className="fw-semibold mb-2">
                {locale === "en"
                  ? "This waitlist form is deprecated."
                  : "Ce formulaire de préinscription est déprécié."}
              </p>
              <p className="small mb-3">
                {locale === "en"
                  ? "Prefer Contact for launch interest (planned modules) or Configure to compose an available package."
                  : "Préférez Contact pour un intérêt lancement (modules prévus) ou Configure pour composer un package disponible."}
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Link
                  className="btn btn-primary btn-sm"
                  to={
                    preselect
                      ? `/contact?product=${encodeURIComponent(preselect)}&type=info`
                      : "/contact?type=info"
                  }
                >
                  {locale === "en" ? "Contact sales →" : "Contact commercial →"}
                </Link>
                <Link className="btn btn-outline-primary btn-sm" to="/configure">
                  {locale === "en" ? "Open configurator →" : "Ouvrir le configurateur →"}
                </Link>
              </div>
              <p className="small text-secondary mt-3 mb-0">
                {locale === "en"
                  ? "You can still submit this form if you already started it."
                  : "Vous pouvez encore envoyer ce formulaire si vous l’avez déjà commencé."}
              </p>
            </div>
            {sent ? (
              <div className="card border-0 shadow-sm p-4 p-md-5 text-center">
                <h2 className="h4 fw-bold mb-3">
                  {locale === "en" ? "You're on the list!" : "Vous êtes inscrit !"}
                </h2>
                <p className="text-secondary mb-4">
                  {locale === "en"
                    ? `Thank you. We registered your interest for: ${selectedLabels.join(", ")}.`
                    : `Merci. Votre intérêt est enregistré pour : ${selectedLabels.join(", ")}.`}
                </p>
                <Link to="/products" className="btn btn-primary">
                  {locale === "en" ? "Back to catalog" : "Retour au catalogue"}
                </Link>
              </div>
            ) : prelaunchProducts.length === 0 ? (
              <div className="alert alert-light border">
                {locale === "en"
                  ? "No module is currently open for pre-registration."
                  : "Aucun module n'est ouvert à la préinscription pour le moment."}{" "}
                <Link to="/products">
                  {locale === "en" ? "Browse available products" : "Voir les produits disponibles"}
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="card border-0 shadow-sm">
                <div className="card-body p-4 p-md-5">
                  <h2 className="h6 fw-bold text-uppercase text-secondary mb-3">
                    {locale === "en" ? "Modules in development" : "Modules en développement"}
                  </h2>
                  <div className="d-flex flex-column gap-2 mb-4">
                    {prelaunchProducts.map((p) => (
                      <label
                        key={p.slug}
                        className="d-flex align-items-start gap-2 border rounded-3 p-3 mb-0"
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-1"
                          checked={selected.includes(p.slug)}
                          onChange={() => toggle(p.slug)}
                        />
                        <span>
                          <span className="fw-semibold">{productDisplayName(p, locale)}</span>
                          <span className={`badge ms-2 ${statusBadgeClass(p.status)}`}>
                            {labelStatus(p.status, locale)}
                          </span>
                          {p.availabilityNote && (
                            <span className="d-block small text-secondary mt-1">
                              {p.availabilityNote}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        {locale === "en" ? "Company *" : "Entreprise *"}
                      </label>
                      <input
                        className="form-control"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        {locale === "en" ? "Contact name *" : "Nom du contact *"}
                      </label>
                      <input
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        {locale === "en" ? "Phone *" : "Téléphone *"}
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        minLength={8}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        {locale === "en" ? "Message (optional)" : "Message (optionnel)"}
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={
                          locale === "en"
                            ? "Your use case, expected timeline…"
                            : "Votre cas d'usage, échéance souhaitée…"
                        }
                      />
                    </div>
                  </div>

                  {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100 mt-4"
                    disabled={submitting || !selected.length}
                  >
                    {submitting
                      ? locale === "en"
                        ? "Sending…"
                        : "Envoi…"
                      : locale === "en"
                        ? "Join the waitlist"
                        : "Me préinscrire"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}